import { PROVIDER_CONFIGS } from '@config/providers/provider.config';
import { AIProviderBase, ProviderKey, ProviderCapabilities, MessageInterface, RequestConfig, FormattedRequest } from '@type/provider';

export class AnthropicProvider implements AIProviderBase {
  readonly id: ProviderKey;
  readonly name: string;
  readonly endpoints: string[];
  readonly models: string[];
  readonly capabilities: ProviderCapabilities;

  constructor() {
    // Get configuration from provider config
    const config = PROVIDER_CONFIGS.anthropic;
    
    this.id = 'anthropic';
    this.name = config.name;
    this.endpoints = config.endpoints;
    this.models = config.models.map(m => m.id);
    this.capabilities = {
      supportsThinking: config.capabilities.supportsThinking,
      maxCompletionTokens: config.models[0]?.maxCompletionTokens ?? 4096,
      defaultModel: config.defaultModel,
      defaultThinkingModel: config.defaultModel
    };
  }
  
  formatRequest(messages: MessageInterface[], config: RequestConfig): FormattedRequest {
    // Extract system message if present
    const systemMessage = messages.find(msg => msg.role === 'system');
    const nonSystemMessages = messages.filter(msg => msg.role !== 'system');
    
    // Format messages for Anthropic API
    const formattedRequest: FormattedRequest = {
      model: config.model,
      max_tokens: config.max_tokens,
      temperature: config.temperature,
      top_p: config.top_p,
      messages: nonSystemMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      })),
      stream: config.stream || false,
      // Add thinking mode if supported and enabled
      ...(config.thinking?.enabled && {
        thinking: {
          type: "thinking_out_loud",
          budget_tokens: config.thinking.budget_tokens
        }
      })
    };
    
    // Add system message as separate property if present
    if (systemMessage) {
      formattedRequest.system = systemMessage.content;
    }
    
    return formattedRequest;
  }

  parseResponse(response: any): string {
    console.debug('[AnthropicProvider] Parsing response:', JSON.stringify(response));
    
    // Check if response is in the standard format we expect
    if (response?.choices?.[0]?.message?.content) {
      return response.choices[0].message.content;
    }
    
    // Handle Anthropic-specific format if it comes through directly
    if (response?.content?.[0]?.text) {
      return response.content[0].text;
    }
    
    // Fallback for any other format
    return '';
  }
  
  parseStreamingResponse(chunk: any): string {
    console.debug('[AnthropicProvider] Parsing streaming chunk:', JSON.stringify(chunk));
    
    // Handle formatted chunks from the API route
    if (chunk.choices && chunk.choices[0] && chunk.choices[0].delta) {
      return chunk.choices[0].delta.content || '';
    }
    
    // Handle raw Anthropic chunks
    if (chunk.type === 'content_block_delta') {
      return chunk.delta?.text || '';
    }
    
    return '';
  }
}
